import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCalendar } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';

const YEARS_PER_PAGE = 24;

@Component({
    selector: 'ceres-calendar-header-navigation',
    standalone: true,
    templateUrl: './calendar-header-navigation.component.html',
    styleUrls: ['./calendar-header-navigation.component.scss'],
    imports: [MatButtonModule, MatIconModule],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class CalendarHeaderNavigationComponent<D> {
    private readonly calendar = inject<MatCalendar<D>>(MatCalendar);
    private readonly dateAdapter = inject<DateAdapter<D>>(DateAdapter);
    private readonly dateFormats = inject<MatDateFormats>(MAT_DATE_FORMATS);

    /** View opened by the month/year label. */
    public readonly periodView = input<'multi-year' | 'year'>('multi-year');

    public constructor() {
        const cdr = inject(ChangeDetectorRef);
        this.calendar.stateChanges.pipe(takeUntilDestroyed()).subscribe(() => cdr.markForCheck());
    }

    public get isMonthView(): boolean {
        return this.calendar.currentView === 'month';
    }

    public get isPreviousDisabled(): boolean {
        // Only clamp in month view; year/multi-year navigation stays open.
        return this.isMonthView && this.isSameMonth(this.calendar.activeDate, this.calendar.minDate);
    }

    public get isNextDisabled(): boolean {
        return this.isMonthView && this.isSameMonth(this.calendar.activeDate, this.calendar.maxDate);
    }

    public get periodLabel(): string {
        if (this.isMonthView) {
            return this.dateAdapter
                .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
                .toLocaleUpperCase();
        }

        return this.dateAdapter.getYearName(this.calendar.activeDate);
    }

    public currentPeriodClicked(): void {
        this.calendar.currentView = this.isMonthView ? this.periodView() : 'month';
    }

    public setNextPeriod(): void {
        this.shiftActiveDate(1);
    }

    public setPreviousPeriod(): void {
        this.shiftActiveDate(-1);
    }

    private shiftActiveDate(direction: 1 | -1): void {
        const { activeDate, currentView } = this.calendar;
        this.calendar.activeDate =
            currentView === 'month'
                ? this.dateAdapter.addCalendarMonths(activeDate, direction)
                : this.dateAdapter.addCalendarYears(
                      activeDate,
                      direction * (currentView === 'year' ? 1 : YEARS_PER_PAGE)
                  );
    }

    private isSameMonth(a: D, b: D | null): boolean {
        return (
            !!b &&
            this.dateAdapter.getYear(a) === this.dateAdapter.getYear(b) &&
            this.dateAdapter.getMonth(a) === this.dateAdapter.getMonth(b)
        );
    }
}
